
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Calendar, Tag, Search, User, ChevronRight } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { TwitterDeliverable } from '@/types/twitterTypes';
import { useToast } from '@/hooks/use-toast';
import { TwitterResultsTable } from './TwitterResultsTable';
import DeliverableTweetsView from './DeliverableTweetsView';
import { useTwitterJobStatus } from '@/hooks/use-twitter-job-status';

const DeliverableDetail = () => {
  const { id } = useParams<{ id: string }>();
  const [deliverable, setDeliverable] = useState<TwitterDeliverable | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [campaign, setCampaign] = useState<{ id: string; name: string } | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<string>('tweets-card');
  const { startBackgroundProcessing } = useTwitterJobStatus(id as string);
  
  useEffect(() => {
    const fetchDeliverableDetails = async () => {
      if (!id) return;
      
      setIsLoading(true);
      try {
        // Fetch deliverable
        const { data: deliverableData, error: deliverableError } = await supabase
          .from('twitter_deliverables')
          .select('*')
          .eq('id', id)
          .single();
        
        if (deliverableError) throw deliverableError;
        setDeliverable(deliverableData as TwitterDeliverable);
        
        // Fetch campaign
        if (deliverableData?.campaign_id) {
          const { data: campaignData, error: campaignError } = await supabase
            .from('twitter_campaigns')
            .select('id, name')
            .eq('id', deliverableData.campaign_id)
            .single();
          
          if (campaignError) throw campaignError;
          setCampaign(campaignData);
        }
      } catch (error) {
        console.error('Error fetching deliverable details:', error);
        toast({
          title: 'Error',
          description: 'Failed to load deliverable details',
          variant: 'destructive'
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchDeliverableDetails();
  }, [id, toast]);
  
  const handleGoBack = () => {
    if (campaign) {
      navigate(`/deliverables/${campaign.id}`);
    } else {
      navigate('/deliverables');
    }
  };
  
  const handleFetchTweets = () => {
    if (!id) return;
    
    startBackgroundProcessing();
    toast({
      title: 'Processing Started',
      description: 'Twitter data processing has started. This may take a few moments.',
    });
  };

  // Parse the content query to highlight OR operators
  const renderContentQuery = () => {
    if (!deliverable?.content_query) return "No query specified";

    return deliverable.content_query.split(/(\s+OR\s+)/g).map((part, index) => {
      if (part.trim() === 'OR') {
        return (
          <Badge key={index} variant="primary" className="mx-1">
            {part}
          </Badge>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };
  
  if (isLoading) {
    return (
      <div className="p-6 flex justify-center items-center min-h-[400px]">
        <div className="animate-pulse">
          <div className="h-12 w-64 bg-secondary rounded mb-6"></div>
          <div className="h-48 w-full max-w-3xl bg-secondary rounded"></div>
        </div>
      </div>
    );
  }
  
  if (!deliverable) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold mb-2">Deliverable Not Found</h2>
          <p className="text-muted-foreground mb-6">This deliverable might have been deleted or does not exist.</p>
          <Button onClick={() => navigate('/deliverables')}>Return to Deliverables</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <Button variant="ghost" onClick={handleGoBack} className="mb-2 p-0 h-8">
            <ArrowLeft className="h-4 w-4 mr-1" />
            <span>Back</span>
          </Button>
          
          <div className="flex flex-wrap items-center gap-1">
            <h1 className="text-2xl font-bold">{deliverable.name}</h1>
            
            {campaign && (
              <div className="flex items-center text-sm text-muted-foreground">
                <ChevronRight className="h-4 w-4" />
                <span>{campaign.name}</span>
              </div>
            )}
          </div>
        </div>
        
        <Button onClick={handleFetchTweets}>Fetch New Tweets</Button>
      </div>
      
      <Card className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-medium mb-4">Search Parameters</h3>
            
            <div className="space-y-4">
              <div className="flex gap-2 items-start">
                <Search className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium text-sm">Query</div>
                  <div className="text-sm break-words p-2 bg-muted/30 rounded mt-1">
                    {renderContentQuery()}
                  </div>
                </div>
              </div>
              
              <div className="flex gap-2 items-start">
                <Tag className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium text-sm">Type</div>
                  <Badge variant="secondary">{deliverable.type}</Badge>
                </div>
              </div>
              
              {deliverable.kol_usernames && deliverable.kol_usernames.length > 0 && (
                <div className="flex gap-2 items-start">
                  <User className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium text-sm">KOL Usernames</div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {deliverable.kol_usernames.map((username, index) => (
                        <Badge key={index} variant="outline">{username}</Badge>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex gap-2 items-start">
                <Calendar className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium text-sm">Date Range</div>
                  <div className="text-sm">
                    {deliverable.start_date && deliverable.end_date ? (
                      <>
                        {format(new Date(deliverable.start_date), 'MMM d, yyyy')} to{' '}
                        {format(new Date(deliverable.end_date), 'MMM d, yyyy')}
                      </>
                    ) : (
                      'No date range specified'
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div>
            <h3 className="font-medium mb-4">Status Information</h3>
            
            <div className="space-y-4">
              <div>
                <div className="font-medium text-sm">Last Updated</div>
                <div className="text-sm">
                  {deliverable.last_fetched ? 
                    format(new Date(deliverable.last_fetched), 'MMM d, yyyy h:mm a') : 
                    'Never fetched'}
                </div>
              </div>
              
              <div>
                <div className="font-medium text-sm">Created</div>
                <div className="text-sm">
                  {format(new Date(deliverable.created_at), 'MMM d, yyyy h:mm a')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>
      
      <Tabs defaultValue="tweets-card" value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="tweets-card">Tweet Cards</TabsTrigger>
          <TabsTrigger value="tweets-table">Tweet Table</TabsTrigger>
        </TabsList>
        
        <TabsContent value="tweets-card" className="mt-6">
          <DeliverableTweetsView deliverableId={id as string} />
        </TabsContent>
        
        <TabsContent value="tweets-table" className="mt-6">
          <TwitterResultsTable deliverableId={id as string} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DeliverableDetail;
